import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, UserCheck } from "lucide-react";

const STATUSES = ["not_started", "in_progress", "completed"];

export default function Employees() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", position: "", department: "", phone: "" });

  const isAdmin = user?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/employees");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/employees", form);
      setShowCreate(false);
      setForm({ name: "", email: "", position: "", department: "", phone: "" });
      load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const updateProgress = async (id, progress) => {
    const status = progress === 0 ? "not_started" : progress >= 100 ? "completed" : "in_progress";
    await api.patch(`/employees/${id}`, { onboarding_progress: Math.min(100, Math.max(0, progress)), onboarding_status: status });
    load();
  };

  return (
    <div className="p-6 lg:p-12" data-testid="employees-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="label-caps text-[#FF2B00]">Workforce</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Employees & Onboarding</h1>
        </div>
        {isAdmin && (
          <button data-testid="employees-new-btn" onClick={() => setShowCreate(true)} className="bg-black text-white px-5 py-3 hover:bg-[#FF2B00] label-caps inline-flex items-center gap-2">
            <Plus size={16} /> Add Employee
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        {items.length === 0 && <div className="bg-white p-12 text-center text-zinc-500 col-span-full" data-testid="employees-empty">No employees yet.</div>}
        {items.map((emp) => (
          <div key={emp.id} className="bg-white p-6" data-testid={`employee-card-${emp.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-xl">{emp.name}</div>
                <div className="text-sm text-zinc-600">{emp.position}</div>
                <div className="text-xs text-zinc-400 mt-1">{emp.email}</div>
              </div>
              <UserCheck size={18} className={emp.onboarding_status === "completed" ? "text-black" : "text-zinc-300"} />
            </div>
            <div className="mt-5">
              <div className="flex justify-between text-xs label-caps text-zinc-500 mb-2">
                <span>Onboarding</span>
                <span>{emp.onboarding_progress}%</span>
              </div>
              <div className="h-2 bg-zinc-100">
                <div className={`h-full ${emp.onboarding_status === "completed" ? "bg-black" : "bg-[#FF2B00]"}`} style={{ width: `${emp.onboarding_progress}%` }} />
              </div>
              {isAdmin && (
                <div className="mt-3 flex gap-2">
                  <button data-testid={`emp-progress-25-${emp.id}`} onClick={() => updateProgress(emp.id, 25)} className="text-xs label-caps border border-zinc-300 px-2 py-1 hover:border-black">25%</button>
                  <button data-testid={`emp-progress-50-${emp.id}`} onClick={() => updateProgress(emp.id, 50)} className="text-xs label-caps border border-zinc-300 px-2 py-1 hover:border-black">50%</button>
                  <button data-testid={`emp-progress-75-${emp.id}`} onClick={() => updateProgress(emp.id, 75)} className="text-xs label-caps border border-zinc-300 px-2 py-1 hover:border-black">75%</button>
                  <button data-testid={`emp-progress-100-${emp.id}`} onClick={() => updateProgress(emp.id, 100)} className="text-xs label-caps bg-black text-white px-2 py-1 hover:bg-[#FF2B00]">Done</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="employee-create-modal">
            <div className="flex items-center justify-between p-5 border-b border-zinc-200">
              <h2 className="font-display text-2xl">Add Employee</h2>
              <button onClick={() => setShowCreate(false)}><X size={20} /></button>
            </div>
            <form onSubmit={create} className="p-6 space-y-4">
              <div><label className="label-caps block mb-1">Name</label>
                <input data-testid="emp-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-black px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Email</label>
                <input data-testid="emp-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border border-black px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Position</label>
                <input data-testid="emp-position" required value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="w-full border border-black px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Department</label>
                <input data-testid="emp-department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" /></div>
              <div><label className="label-caps block mb-1">Phone</label>
                <input data-testid="emp-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border border-zinc-300 px-3 py-2" /></div>
              {error && <div className="text-sm text-[#FF2B00]">{error}</div>}
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowCreate(false)} className="border border-black px-5 py-2 label-caps">Cancel</button>
                <button data-testid="emp-create-submit" type="submit" className="bg-black text-white px-5 py-2 hover:bg-[#FF2B00] label-caps">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
